/*
  hw-attribution.js -- loaded (defer) only on the HandWriter sample form page,
  templates/page.handwriter-sample.json (https://bantamtools.com/pages/handwriter-sample).

  Copies the visitor's utm_* and hw_cta query parameters, plus the referring page
  (origin + path only), into the lead form's "Source" field, then visually hides that
  field. Shopify Forms has no hidden field type, so "Source" is an ordinary optional
  single-line text field (stored as the custom.source customer metafield).

  The form renders inside the <shopify-forms-embed> element's shadow root, so theme CSS
  and a plain document.querySelector cannot reach it; this script reaches through
  .shadowRoot. A MutationObserver on the document sees the embed element arrive but NOT
  mutations inside its shadow root, so a 250ms poll runs alongside it. Both stop on
  success or after 10 seconds.

  Fail-soft: if Shopify changes the embed markup or closes the shadow root, the field
  stays visible and empty, and submissions still work.

  The field's DOM input name is not documented (custom.source is the metafield key, not
  necessarily the input name), so the input is found by several candidate names and
  then by its visible label text, "Source".
*/
(function () {
  'use strict';

  var MAX_LENGTH = 1000;
  var TIMEOUT_MS = 10000;
  var POLL_MS = 250;
  var KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'hw_cta'];
  var NAME_SELECTORS = [
    'input[name="custom#source"]',
    'input[name="custom.source"]',
    'input[name="custom[source]"]',
    'input[name="source"]'
  ];

  function buildValue() {
    var params = new URLSearchParams(window.location.search);
    var out = new URLSearchParams();
    KEYS.forEach(function (key) {
      var v = params.get(key);
      if (v) out.append(key, v.slice(0, 200));
    });
    if (document.referrer) {
      // Origin + path only: the referrer's query string can carry personal data
      // (emails, tokens, search terms) that has no place in a lead record.
      try {
        var ref = new URL(document.referrer);
        out.append('referrer', ref.origin + ref.pathname);
      } catch (e) { /* unparseable referrer: omit it */ }
    }
    return clamp(out.toString(), MAX_LENGTH);
  }

  // Slice to limit, then drop a trailing partial percent-escape ("%" or "%X") the
  // cut may have left, so the stored value always decodes cleanly.
  function clamp(value, limit) {
    if (value.length <= limit) return value;
    return value.slice(0, limit).replace(/%[0-9A-Fa-f]?$/, '');
  }

  function findByLabel(root) {
    var labels = root.querySelectorAll('label');
    for (var i = 0; i < labels.length; i++) {
      var text = (labels[i].textContent || '').replace(/\s+/g, ' ').trim();
      if (!/^source\b/i.test(text)) continue;
      var input = null;
      if (labels[i].htmlFor) input = root.getElementById ? root.getElementById(labels[i].htmlFor) : null;
      if (!input) input = labels[i].querySelector('input');
      if (input && input.tagName === 'INPUT') return input;
    }
    return null;
  }

  function findSourceInput(root) {
    for (var i = 0; i < NAME_SELECTORS.length; i++) {
      var el = root.querySelector(NAME_SELECTORS[i]);
      if (el) return el;
    }
    return findByLabel(root) ||
      root.querySelector('input[aria-label="Source" i], input[placeholder="Source" i]');
  }

  // The highest ancestor that still contains no form control other than this input:
  // the field's own wrapper (label + input + help text), never the whole form. iframes
  // count as controls so hiding never moves the form's captcha frame.
  function fieldWrapper(input, root) {
    var node = input;
    while (node.parentElement && node.parentElement !== root) {
      var controls = node.parentElement.querySelectorAll('input, select, textarea, button, iframe');
      if (controls.length > 1) break;
      node = node.parentElement;
    }
    return node;
  }

  function hide(input, root) {
    var wrap = fieldWrapper(input, root);
    var st = wrap.style;
    st.position = 'absolute';
    st.left = '-10000px';
    st.width = '1px';
    st.height = '1px';
    st.overflow = 'hidden';
    wrap.setAttribute('aria-hidden', 'true');
    wrap.inert = true;
    input.setAttribute('tabindex', '-1');
  }

  var value = buildValue();
  var done = false;

  function trySet() {
    if (done) return true;
    var hosts = document.querySelectorAll('shopify-forms-embed');
    for (var i = 0; i < hosts.length; i++) {
      var root = hosts[i].shadowRoot;
      if (!root) continue;
      var input = findSourceInput(root);
      if (!input) continue;
      // Never overwrite something the visitor typed before this ran.
      if (value && !input.value) {
        var v = input.maxLength > 0 ? clamp(value, input.maxLength) : value;
        var setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;
        setter.call(input, v);
        input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
        input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
      }
      hide(input, root);
      done = true;
      return true;
    }
    return false;
  }

  if (trySet()) return;

  var deadline = Date.now() + TIMEOUT_MS;
  var observer = null;
  var timer = null;

  function stop() {
    if (observer) { observer.disconnect(); observer = null; }
    if (timer) { clearInterval(timer); timer = null; }
  }

  function tick() {
    if (trySet() || Date.now() > deadline) stop();
  }

  if (window.MutationObserver) {
    observer = new MutationObserver(tick);
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
  timer = setInterval(tick, POLL_MS);
})();
